import React from 'react'
import { HashLink } from 'react-router-hash-link'

import { ANCHOR_IDS } from '@constants/anchorIds'
import {
  Banner,
  Button,
  ConfirmModal,
  Icon,
  Tooltip,
  useModalContext,
} from '@fourthwall/components'
import { RefreshIcon, TickIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import * as S from './MailchimpStoreConnected.styled'

type Props = {
  createSyncJob: () => Promise<void>
  isCreatingSyncJob: boolean
  hasSynced: boolean
  recentSync: boolean
  abandonedCheckoutIsOff: boolean
  dataCount: number
}

export function blockSyncMessage(hasSynced: boolean, blockSync: boolean) {
  if (hasSynced) {
    return 'Your data was already synced.'
  }

  if (blockSync) {
    return (
      <S.BlockSyncMessage>
        <p>
          You’re about to sync a large subscriber list. This will significantly increase your
          monthly Mailchimp charges.
        </p>
        <p>
          If you still want to sync this data, please contact support@fourthwall.com for support.
        </p>
      </S.BlockSyncMessage>
    )
  }

  return undefined
}

const MAILCHIMP_MODAL_THRESHOLD = 500
const MAILCHIMP_BLOCK_SYNC_THRESHOLD = 10000

export const MailchimpStoreConnected = ({
  createSyncJob,
  isCreatingSyncJob,
  hasSynced,
  abandonedCheckoutIsOff,
  dataCount,
}: Props) => {
  const { open } = useModalContext()

  const handleSyncClick = () => {
    if (dataCount > MAILCHIMP_MODAL_THRESHOLD) {
      open(ConfirmModal, {
        title: 'Sync customer data with Mailchimp?',
        text: 'This will sync your Fourthwall customer data to Mailchimp. This action may take a few minutes to complete and can increase your monthly Mailchimp charges.',
        confirmLabel: 'Sync Now',
        confirmAppearance: 'primary',
        onConfirm: createSyncJob,
      })
    } else {
      createSyncJob()
    }
  }

  const blockSync = dataCount > MAILCHIMP_BLOCK_SYNC_THRESHOLD

  return (
    <S.Container>
      <S.Title>
        <Icon component={TickIcon} width={24} height={24} />
        <div>Successfully Connected to Mailchimp</div>
      </S.Title>

      <S.Description>
        Your Fourthwall shop is now connected to Mailchimp with ecommerce features enabled. Customer
        data will automatically sync and you can create targeted email campaigns based on purchase
        behavior.
      </S.Description>

      <S.SyncSection>
        <Tooltip
          reference={
            <Button
              label="Sync Now"
              appearance="primary"
              size="medium"
              leftIcon={<Icon component={RefreshIcon} width={16} height={16} />}
              onClick={handleSyncClick}
              loading={isCreatingSyncJob}
              disabled={isCreatingSyncJob || hasSynced || blockSync}
            />
          }
        >
          {blockSyncMessage(hasSynced, blockSync)}
        </Tooltip>
      </S.SyncSection>

      {!abandonedCheckoutIsOff && (
        <Banner appearance="warning">
          Fourthwall's default abandoned cart emails are turned on. If you're using Mailchimp to
          send abandoned cart emails make sure to{' '}
          <HashLink
            to={{
              pathname: routing.settings.checkout,
              hash: ANCHOR_IDS.ABANDONED_CHECKOUT_REMINDERS,
            }}
          >
            disable Fourthwall cart abandonment emails
          </HashLink>
          .
        </Banner>
      )}

      {/* <S.Footer>
        Need help getting started? Visit our{' '}
        <ButtonClean
          label="Help Center"
          href={routing.external.helpCenter.mailchimp}
          target="_blank"
        />{' '}
        for detailed guides and best practices.
      </S.Footer> */}
    </S.Container>
  )
}
